import { DependencyList, useCallback, useEffect, useRef, useState } from 'react';
import { useBlocker, useRouter } from '@frontend/weave-router';
import { NavigationContext, ShouldBlockFn, useNonReactiveInterRouter } from './inter-router';
import { useSettingsNavigate } from './use-settings-navigate';

export type UseNavigationBlockerProps = {
  shouldBlockFn: ShouldBlockFn;
  enableBeforeUnload?: boolean;
};

/**
 * Custom hook to block navigation attempts in both the main app router and our custom settings router.
 *
 * This hook integrates with Tanstack Router's `useBlocker` for the main app routes and a custom
 * inter-router store for blocking navigation in the settings routes. It provides mechanisms to
 * proceed or cancel blocked navigation attempts, and exposes the blocking state and context.
 *
 * @param props - Configuration object for navigation blocking.
 * @param props.shouldBlockFn - Function that determines whether navigation should be blocked, given the current and next navigation contexts.
 *  If true, navigation attempts will be blocked.
 * @param props.enableBeforeUnload - If true, enables blocking navigation on browser unload events.
 * @param deps - Dependency list for the effect that sets the blocking function in the inter-router store. Should be set with any variables used in the shouldBlockFn
 *
 * @returns An object containing:
 * - `proceed`: Function to continue with the blocked navigation attempt.
 * - `cancel`: Function to cancel the blocked navigation attempt.
 * - `isBlocked`: Boolean indicating whether navigation is currently blocked.
 * - `blockedContext`: The context of the blocked navigation attempt, if any.
 *
 * @remarks
 * - The hook manages blocking state and context for both routers.
 * - When navigation is blocked, the blocking function and context are set.
 * - Calling `proceed` will navigate to the attempted route and reset the blocking state.
 * - Calling `cancel` will discard the blocked navigation attempt and reset the blocking state.
 */
export const useNavigationBlocker = (
  { shouldBlockFn, enableBeforeUnload = false }: UseNavigationBlockerProps,
  deps: DependencyList
) => {
  const nonReactiveInterRouter = useNonReactiveInterRouter();
  const { navigate: settingsNavigate } = useSettingsNavigate();
  const router = useRouter();

  const blockedContextRef = useRef<{ current: NavigationContext; next: NavigationContext }>();
  const [isBlocked, setIsBlocked] = useState(false);

  const createNavigationContext = useCallback(
    (current: any, next: any, isSettingsRouter = false): { current: NavigationContext; next: NavigationContext } => ({
      current: {
        fullPath: current.fullPath,
        params: current.params,
        pathname: current.pathname,
        search: current.search,
        isHandledBySettingsRouter: isSettingsRouter,
      },
      next: {
        fullPath: next.fullPath,
        params: next.params,
        pathname: next.pathname,
        search: next.search,
        isHandledBySettingsRouter: isSettingsRouter,
      },
    }),
    []
  );

  // Main app router blocker. Use Tanstack Router's native useBlocker hook to block
  // navigation attempts to routes in the main app (non-settings routes).
  const { proceed: mainAppRouterProceed } = useBlocker({
    shouldBlockFn: ({ current, next }) => {
      const context = createNavigationContext(current, next, false);
      const shouldBlock = shouldBlockFn(context);

      if (shouldBlock) {
        blockedContextRef.current = context;
        setIsBlocked(true);
      }

      return shouldBlock;
    },
    enableBeforeUnload,
    withResolver: true,
  });

  // Set the shouldBlockFn in the inter-router zustand store. This is to block our custom
  // settings router navigation.
  useEffect(() => {
    if (!shouldBlockFn) return;

    const { setShouldBlockFn } = nonReactiveInterRouter.getState();

    const wrappedShouldBlock = (args: { current: NavigationContext; next: NavigationContext }) => {
      const shouldBlock = shouldBlockFn(args);

      if (shouldBlock) {
        blockedContextRef.current = args;
        setIsBlocked(true);
      }

      return shouldBlock;
    };

    setShouldBlockFn(wrappedShouldBlock);
  }, deps);

  // useEffect that is used to cleanup the state when the component unmounts
  useEffect(() => {
    const { setShouldBlockFn } = nonReactiveInterRouter.getState();

    return () => {
      setShouldBlockFn(undefined);
      blockedContextRef.current = undefined;
      setIsBlocked(false);
    };
  }, []);

  /**
   * Handles the navigation proceed action after having blocking a navigation attempt. It
   * will navigate to the latest attempted route. If no route was attempted, it does nothing.
   *
   * This function checks if there is a blocked navigation context and, if so,
   * resets the blocking state and proceeds with navigation. If the next route
   * is handled by the settings router, it navigates using `settingsNavigate`.
   * Otherwise, it uses Tanstack router's proceed function to continue with the main app router.
   *
   * Side effects:
   * - Clears the blocking function in the router state if set.
   * - Resets the blocked context reference and blocked state.
   */
  const proceed = useCallback(() => {
    const blockedContext = blockedContextRef.current;

    if (!blockedContext?.next) {
      return;
    }

    const { setShouldBlockFn, shouldBlockFn } = nonReactiveInterRouter.getState();
    const { next } = blockedContext;

    if (shouldBlockFn) {
      setShouldBlockFn(undefined);
    }

    blockedContextRef.current = undefined;
    setIsBlocked(false);

    if (next.isHandledBySettingsRouter) {
      settingsNavigate({
        to: next.fullPath as any,
        params: next.params,
        search: next.search,
      });
    } else {
      mainAppRouterProceed?.();
    }
  }, [settingsNavigate, router, mainAppRouterProceed]);

  const cancel = useCallback(() => {
    blockedContextRef.current = undefined;
    setIsBlocked(false);
  }, []);

  return {
    proceed,
    cancel,
    isBlocked,
    blockedContext: blockedContextRef.current,
  };
};
